import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { baseApi } from '@/Redux/baseApi';
import { useAppDispatch } from '@/Redux/store';
import { usePermission } from '@/Hooks/usePermission';
import {
  invalidateBoards,
  invalidateOrder,
  useKitchenUnroutedQuery,
  type Station,
  type UnroutedLine,
} from '@/Redux/Slices/operationsApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { apiMessage, formatDuration, formatQuantity, useNow } from './shared';

/**
 * The two fixes, which are two different acts.
 *
 * `once` gets tonight's food cooked and leaves the menu alone — `kitchen.update`.
 * `always` edits the PRODUCT so the next order routes itself — `menu.update`,
 * and the one Saher actually asked for. They are one dialog because an operator
 * looking at a stuck line is choosing between them, and two buttons with no
 * shared context is how "always" gets pressed by accident.
 */
type Scope = 'once' | 'always';

/**
 * S-32 — the lane for lines that reached no kitchen.
 *
 * WHY IT EXISTS AT ALL: kitchen routing comes from the product's `stationId`,
 * and the send path skips any line without one. Such a line gets no ticket, and
 * every board in this app is per-station, so it appeared on no screen anywhere:
 * the order was accepted, the floor believed the food was ordered, the kitchen
 * saw nothing. Measured on this venue: 120 products with a null station meant
 * every order produced zero tickets.
 *
 * WHY HERE AND NOT A SEPARATE PAGE: a cook never navigates. This renders above
 * the board on the kitchen screen itself — and above the station picker, which
 * is where a KDS lands before it is pinned — so it is seen without knowing to
 * look for it. It is branch-level, unlike everything else here, because a line
 * with no station belongs to no station and hiding it under one would be a lie.
 *
 * It draws nothing when nothing is stuck. Products that are stationless ON
 * PURPOSE — the syrups and add-ons, an hour of PlayStation — never appear:
 * this asks about SENT LINES, not about the catalogue.
 */
export function UnroutedLane({ branchId, stations }: { branchId: string; stations: Station[] }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const dispatch = useAppDispatch();
  const now = useNow(1000);

  const [open, setOpen] = useState(true);
  const [target, setTarget] = useState<UnroutedLine | null>(null);
  const [stationId, setStationId] = useState('');
  const [scope, setScope] = useState<Scope>('once');
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => ({ branchId, limit: 100 }), [branchId]);
  const { data, isLoading, error } = useKitchenUnroutedQuery(params, {
    refetchOnMountOrArgChange: true,
  });
  const lines = data?.rows ?? [];

  const canRoute = can('kitchen.update');
  const canEditMenu = can('menu.update');

  // A cook who cannot edit the menu is never shown "always" pre-selected; the
  // dialog opens on the fix they are allowed to make.
  useEffect(() => {
    if (!target) return;
    setStationId(stations[0]?.id ?? '');
    setScope(canEditMenu && target.canRouteAlways ? 'always' : 'once');
  }, [target, stations, canEditMenu]);

  const alwaysBlocked = target
    ? !target.canRouteAlways
      ? 'notPrepared'
      : !canEditMenu
        ? 'permission'
        : null
    : null;

  const close = () => {
    setTarget(null);
    setBusy(false);
  };

  const submit = async () => {
    if (!target || !stationId) return;
    setBusy(true);
    try {
      /**
       * Two requests on purpose, not one endpoint that does both.
       *
       * The catalog write is `menu.update` and the routing write is
       * `kitchen.update`; folding them into one route would mean one guard for
       * two authorities, and a cook who may put tonight's dish on a board would
       * silently gain the right to change what that product does tomorrow.
       *
       * The menu goes first: if it is refused, nothing has happened yet and the
       * operator can fall back to "this order only". If the ROUTING half failed
       * after the menu succeeded, the product is still fixed for next time —
       * the better of the two half-states to be left in.
       */
      if (scope === 'always') {
        await api.patch(`/catalog/${target.itemId}`, { stationId });
        dispatch(
          baseApi.util.invalidateTags([
            { type: 'Catalog', id: 'LIST' },
            { type: 'Catalog', id: target.itemId },
          ]),
        );
      }
      await api.post(`/kitchen/items/${target.id}/route`, { stationId });

      const stationName = stations.find((s) => s.id === stationId)?.name ?? '';
      toast.success(
        scope === 'always'
          ? t('operations.kitchen.unrouted.routedAlways', {
              name: target.catalogName,
              station: stationName,
            })
          : t('operations.kitchen.unrouted.routedOnce', { station: stationName }),
      );
      dispatch(invalidateOrder(target.orderId));
      dispatch(invalidateBoards());
      close();
    } catch (error) {
      toast.error(apiMessage(error, t('operations.errors.actionFailed')));
      setBusy(false);
    }
  };

  /**
   * A failed read is the ONE thing this lane may not swallow.
   *
   * Rendering nothing is how a stuck line looked before any of this existed, so
   * "the check itself is down" and "nothing is stuck" must not draw the same
   * screen. One line, no retry button — the query refetches on its own.
   */
  if (error) {
    return (
      <p className="rounded-lg border border-amber-400 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
        {t('operations.kitchen.unrouted.checkFailed')}
      </p>
    );
  }

  // Silence is the correct output: nothing is stuck.
  if (isLoading || !lines.length) return null;

  return (
    <>
      <div className="rounded-xl border border-red-400 bg-red-50 dark:border-red-900 dark:bg-red-950/40">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2 p-3 text-start"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-red-700 dark:text-red-300" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-red-700 dark:text-red-300 rtl:-scale-x-100" />
          )}
          <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-red-800 dark:text-red-200">
              {t('operations.kitchen.unrouted.title', { count: lines.length })}
            </div>
            <div className="text-xs text-red-700 dark:text-red-300">
              {t('operations.kitchen.unrouted.hint')}
            </div>
          </div>
        </button>

        {open && (
          <ul className="space-y-2 border-t border-red-200 p-3 dark:border-red-900">
            {lines.map((line) => {
              const waited = Math.max(
                0,
                Math.floor((now - new Date(line.sentAt).getTime()) / 1000),
              );
              return (
                <li
                  key={line.id}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-red-200 bg-surface p-3 dark:border-red-900"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium">
                      {formatQuantity(line.quantity)} × {line.name}
                    </div>
                    <div className="text-xs text-ink-muted">
                      {t('operations.kitchen.orderRef', { number: line.orderNumber })}
                      {line.customerName ? ` · ${line.customerName}` : ''}
                      {' · '}
                      <span dir="ltr">{formatDuration(waited)}</span>
                    </div>
                    {!!line.modifiers.length && (
                      <div className="truncate text-xs text-ink-muted">
                        {line.modifiers.map((m) => `${m.name} ×${m.quantity}`).join(' · ')}
                      </div>
                    )}
                    {line.notes && (
                      <div className="text-xs text-amber-700 dark:text-amber-400">{line.notes}</div>
                    )}
                    <Badge tone="danger" className="mt-1">
                      {t('operations.kitchen.unrouted.badge')}
                    </Badge>
                  </div>

                  <Button
                    size="sm"
                    disabled={!canRoute || !stations.length}
                    title={
                      !canRoute
                        ? t('operations.kitchen.blocked.permission', { permission: 'kitchen.update' })
                        : !stations.length
                          ? t('operations.kitchen.noStations')
                          : undefined
                    }
                    onClick={() => setTarget(line)}
                  >
                    {t('operations.kitchen.unrouted.fix')}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Dialog
        open={!!target}
        onClose={close}
        title={t('operations.kitchen.unrouted.dialogTitle', { name: target?.name ?? '' })}
        description={t('operations.kitchen.unrouted.dialogHint')}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={busy || !stationId}>
              {t('operations.kitchen.unrouted.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('operations.kitchen.station')}>
            <Select value={stationId} onChange={(e) => setStationId(e.target.value)}>
              {stations.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>

          {/*
            Radios, not two buttons. "Always" edits the MENU — a different
            authority and a different blast radius from cooking one dish — so
            the operator has to see both options side by side and read what the
            other one would have done before committing to either.
          */}
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium">
              {t('operations.kitchen.unrouted.scopeLabel')}
            </legend>

            <label className="flex cursor-pointer items-start gap-2 rounded-lg border p-3">
              <input
                type="radio"
                className="mt-1"
                name="unrouted-scope"
                checked={scope === 'once'}
                onChange={() => setScope('once')}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">
                  {t('operations.kitchen.unrouted.scope.once')}
                </span>
                <span className="block text-xs text-ink-muted">
                  {t('operations.kitchen.unrouted.scope.onceHint', {
                    name: target?.catalogName ?? '',
                  })}
                </span>
              </span>
            </label>

            <label
              className={
                alwaysBlocked
                  ? 'flex items-start gap-2 rounded-lg border border-dashed p-3 opacity-60'
                  : 'flex cursor-pointer items-start gap-2 rounded-lg border border-amber-400 bg-amber-50 p-3 dark:bg-amber-950/40'
              }
            >
              <input
                type="radio"
                className="mt-1"
                name="unrouted-scope"
                disabled={!!alwaysBlocked}
                checked={scope === 'always'}
                onChange={() => setScope('always')}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">
                  {t('operations.kitchen.unrouted.scope.always')}
                </span>
                <span className="block text-xs text-ink-muted">
                  {alwaysBlocked
                    ? t(`operations.kitchen.unrouted.alwaysBlocked.${alwaysBlocked}`, {
                        type: target?.catalogType ?? '',
                      })
                    : t('operations.kitchen.unrouted.scope.alwaysHint', {
                        name: target?.catalogName ?? '',
                      })}
                </span>
                {!alwaysBlocked && (
                  <span className="mt-1 block text-xs font-medium text-amber-800 dark:text-amber-300">
                    {t('operations.kitchen.unrouted.scope.alwaysWarning')}
                  </span>
                )}
              </span>
            </label>
          </fieldset>
        </div>
      </Dialog>
    </>
  );
}
